import { useState, useEffect, useRef, useCallback, memo } from "react";
import { saveAs } from "file-saver";

// prettier-ignore
import { Tooltip, Box, Divider, IconButton, Card, CardContent, CardHeader, Typography, Table, TextField, DialogActions, TableContainer,
          TableHead, TableBody, Paper, Accordion, AccordionSummary, Menu, Button, AccordionDetails, CircularProgress, Skeleton,
          Checkbox, ToggleButtonGroup, ToggleButton, List, ListItem, ListItemIcon, ListItemText, Collapse, Dialog, DialogTitle,
          DialogContent, DialogContentText, Pagination, MenuItem, FormControl, Select, Backdrop, InputLabel, } from "@mui/material";
// prettier-ignore
import { FileDownload, Delete, ViewList, GridView, ExpandLess, Pending, CheckCircle, ErrorOutline, DateRangeOutlined,
          ExpandMore, CheckCircleOutline, CloudCircle, Description, ModelTraining, CloudUpload, DirectionsBoat, Article,
          VisibilityOff, DangerousOutlined, Close, InfoOutlined, SyncDisabledOutlined, Sync, Edit, SensorOccupied,
          Person, Build, TableChartOutlined, Unpublished, PendingActions, Search, FilterAltOff, SouthAmerica,
          DescriptionOutlined, Refresh, TaskAlt,  Engineering, } from "@mui/icons-material";

import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import { visuallyHidden } from "@mui/utils";

import _ from "underscore";
import moment from "moment";

// prettier-ignore
import { C, D, SearchField, TRow, TCell, TSortLabel, ConfirmDialog, CustomSelect, Input,
        checker, MessageDialog, exportStandardExcel, openLink, 
} from "../include";

moment.locale("pt_br");

// Limite de uploads simultâneos em updatepipe()
const MAX_CONCURRENT_UPLOADS = 4;
const LARGE_MEDIATECA_THRESHOLD = 3000; // limite ajustável indica muitos docs para exportar
const DEFAULT_PAGE_SIZE = 25; // num de docs por pagina por default

const DarkTooltip = (tooltipProps) => (
  <Tooltip
    {...tooltipProps}
    arrow
    placement="top"
    componentsProps={{
      tooltip: {
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          color: "#fff",
          fontSize: "0.75rem",
          borderRadius: "4px",
          padding: "4px 8px",
        },
      },
      arrow: { sx: { color: "rgba(0, 0, 0, 0.8)" } },
    }}
  />
);

const UploadListCard = ({ title, status }) => (
  <D col>
    <D className="row aC" style={{ padding: "0.5rem 0" }}>
      <Description sx={{ color: "#ccc", paddingRight: "5px" }} />
      <span className="th">{title}</span>
      {status === "Pending" && <Pending sx={{ color: "#ccc", ml: "auto" }} />}
      {status === "Uploading" && <CloudCircle sx={{ color: "#ffb300", ml: "auto" }} />}
      {status === "Done" && <CheckCircle sx={{ color: "green", ml: "auto" }} />}
      {status === "Error" && <DangerousOutlined sx={{ color: "red", ml: "auto" }} />}
    </D>
    <Divider />
  </D>
);


// ====================================================
// COMPONENTE QUE APRESENTA O PAINEL DE METADADOS DO DOCUMENTO
// ====================================================
export const MediatecaQualificators = ({ ocs }) => (
  <D className="col e aF">
    {ocs?.length > 0 &&
      ocs.map((o) => (
        <D col key={o.qualificador_nome} style={{ padding: "7px" }}>
          <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: "bold" }}>
            {o.qualificador_nome}{" "}
            {o.quantificador_homem && (
              <Box component="span" sx={{ fontWeight: "normal" }}>
                {`(ed: ${new Date(o.updated_at).toLocaleString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })})`}
              </Box>
            )}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            style={o.quantificador_homem ? { fontWeight: "bold" } : {}}
          >
            {o.quantificador_homem || o.quantificador_maquina}
          </Typography>
        </D>
      ))}
  </D>
);

// ====================================================
// componente que apresenta o form de edição de ocorrencias
// ====================================================
function MediatecaEdit({
  initialQualificador = "",
  initialQuantificador = "",
  initialToolSelected = "old",
  ocs = [],
  facts = [],
  embarcacoes = [],
  grupos = [],
  docId,
  alert,
  dialogClose,
  updateOcs,
  updateMediateca,
  api,
}) {

  // Verifica se o qualificador do atalho já existe no documento.
  // Se existir, forçamos a aba para "old" (Existente), ignorando o initialToolSelected do pai se vier errado.
  const [toolSelected, setToolSelected] = useState(() => {
    const jaExiste = ocs.some((o) => o.qualificador_nome === initialQualificador);
    return jaExiste ? "old" : (initialToolSelected || "old");
  });

  const [qualificador, setQualificador] = useState(initialQualificador || "");

  // QUANTIFICADOR INTELIGENTE:
  const [quantificador, setQuantificador] = useState(() => {
    // Se o pai mandou um valor explícito, usa ele
    if (initialQuantificador) return initialQuantificador;

    // Se o pai mandou só o atalho (qualificador), tentamos achar o valor já salvo nas ocorrências!
    if (initialQualificador) {
      const oc = ocs.find((o) => o.qualificador_nome === initialQualificador);
      return oc?.quantificador_homem || oc?.quantificador_maquina || "";
    }

    return "";
  });

  // ESTADO DERIVADO CALCULADO NA HORA (Evita bugs de dessincronização)
  const oldOpts = _.pluck(ocs, "qualificador_nome").sort((a, b) => a.localeCompare(b));
  const newOpts = _.difference(_.pluck(facts, "qualificator"), oldOpts);
  const qualiOpts = { old: oldOpts, new: newOpts };

  // Busca os dados da ocorrência selecionada
  const currentOc = ocs.find((o) => o.qualificador_nome === qualificador);
  const currentFact = facts.find((f) => f.qualificator === qualificador);

  const quantificadorMaquina = currentOc?.quantificador_maquina || "";
  const quantificadorHomem = currentOc?.quantificador_homem || "";

  // Lida com a mudança no Dropdown
  const handleQualificadorChange = (v) => {
    setQualificador(v);

    if (toolSelected === "old") {
      const oc = ocs.find((o) => o.qualificador_nome === v);
      // Se ele trocou no dropdown, busca o valor que está no banco para preencher o input
      const val = oc?.quantificador_homem || oc?.quantificador_maquina || "";
      setQuantificador(val);
    } else {
      setQuantificador("");
    }
  };

  const handleConfirm = () => {
    if (!docId || !qualificador || !quantificador) {
      alert("Preencha todos os campos para confirmar !");
      return;
    }

    let bReadyToGo = true;
    const strType = currentOc?.tipo || "string";

    // VAKIDAÇÕES ESPECIAIS PARA DETERMINADOS FATOS
    switch (qualificador) {
    // --- Validação especial para "Data Aproximada" ---
      case "Data Aproximada": {
        if (strType !== "string") {
          bReadyToGo = false;
          alert("Data Inválida ! Datas devem ter o formato dd/mm/aaaa.\nPara inserção de texto use o fato 'Anotações'.");
        } else {
          const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
          const match = quantificador.match(dateRegex);

          if (!match) {
            bReadyToGo = false;
            alert("Data Inválida ! Datas devem ter o formato dd/mm/aaaa.\nPara inserção de texto use o fato 'Anotações'.");
          } else {
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10);
            const year = parseInt(match[3], 10);

            if (month < 1 || month > 12) {
              bReadyToGo = false;
              alert("Data Inválida ! Datas devem ter o formato dd/mm/aaaa.\nPara inserção de texto use o fato 'Anotações'.");
            } else {
              const daysInMonth = new Date(year, month, 0).getDate();
              if (day < 1 || day > daysInMonth) {
                bReadyToGo = false;
                alert("Data Inválida ! Datas devem ter o formato dd/mm/aaaa.\nPara inserção de texto use o fato 'Anotações'.");
              }
            }
          }
        }
        break;
      }

      case "Pedido de Reparo": {
        const reparoRegex = /^\s*\d{1,6}\s*(,\s*\d{1,6}\s*)*$/;
        if (!reparoRegex.test(quantificador)) {
          bReadyToGo = false;
          alert("Pedido de Reparo inválido!\nBlocos com máximo de 6 dígitos separados por vírgula.\nPara inserção de texto use o fato 'Anotações'.");
        }
        break;
      }

      case "Ordem de Manutenção": {
        const ordemRegex = /^\s*\d{1,8}\s*(,\s*\d{1,8}\s*)*$/;
        if (!ordemRegex.test(quantificador)) {
          bReadyToGo = false;
          alert("Ordem de Manutenção inválida!\nBlocos com máximo de 8 dígitos separados por vírgula.\nPara inserção de texto use o fato 'Anotações'.");
        }
        break;
      }

      default: {
        // Validação fallback (padrão) para campos do tipo date genéricos
        if (strType === "date") {
          let [day, month, year] = quantificador.split("/");
          var dtConversion = new Date(+year, +month - 1, +day);
          var bIsDate = dtConversion instanceof Date && !isNaN(dtConversion);

          if (!bIsDate) {
            bReadyToGo = false;
            alert("Data Inválida ! Datas devem ter o formato dd/mm/aaaa.\nPara inserção de texto use o fato 'Anotações'.");
          }
        }
        break;
      }
    }

    // Não pode string em branco
    var v = quantificador.trim();
    if (v.length === 0) {
      bReadyToGo = false;
      alert("Valor não pode ser branco ou vazio !");
    }

    if (bReadyToGo) {
      // 🔹 Sanitização do valor
      const sanitizedQuantificador = quantificador
        .replace(/['"`;]/g, " ")
        .replace(/--/g, " ")
        .replace(/\\/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      api
        .post("mediateca/ocorrencias", {
          doc_id: docId,
          qualificador: currentFact?.id,
          qualificador_nome: qualificador,
          quantificador: sanitizedQuantificador,
        })
        .then((r) => {
          updateOcs();
          dialogClose();
          if (r.status === "ok") {
            alert("Ocorrência atualizada com sucesso.");
            updateMediateca();
          } else {
            alert("Erro ao atualizar a ocorrência !");
          }
        });
    }
  };

  const handleRemoveOccurrence = () => {
    api
      .post("mediateca/deleteoccurrence", {
        documento_id: docId,
        fato_id: currentFact?.id,
      })
      .then((r) => {
        updateOcs();
        dialogClose();
        if (r.status === "ok") {
          alert("Ocorrência excluída com sucesso.");
          updateMediateca();
        } else {
          alert("Erro ao excluir a ocorrência !");
        }
      });
  };

  const handleRemoveValueOccurrence = () => {
    // Se o valor da máquina estiver vazio, remove a ocorrência completa
    if (currentOc && currentOc.quantificador_maquina === "") {
      handleRemoveOccurrence();
      return;
    }

    api
      .post("mediateca/deletevalureoccurence", {
        documento_id: docId,
        fato_id: currentFact?.id,
      })
      .then((r) => {
        updateOcs();
        dialogClose();
        if (r.status === "ok") {
          alert("Valor excluído com sucesso.");
          updateMediateca();
        } else {
          alert("Erro ao excluir o valor !");
        }
      });
  };

  return (
    <>
      <ConfirmDialog
        title="Editar Metadados do Documento"
        onCancel={dialogClose}
        onConfirm={handleConfirm}
        onDeleteAction={qualificador ? handleRemoveOccurrence : null}
      >
        <D h="1rem" />
        <ToggleButtonGroup
          color="primary"
          className="row w aF"
          style={{ minWidth: "15em" }}
          value={toolSelected}
          exclusive
          onChange={(e) => {
            setToolSelected(e.target.value);
            setQualificador("");
            setQuantificador("");
          }}
        >
          <ToggleButton className="e" value="old"> Existente </ToggleButton>
          <ToggleButton className="e" value="new"> Novo </ToggleButton>
        </ToggleButtonGroup>

        <CustomSelect
          label="Fato"
          margin="normal"
          value={qualificador}
          options={qualiOpts[toolSelected]}
          onChange={handleQualificadorChange}
        />

        {!!qualificador && (
          <>
            {toolSelected === "old" && (
              <D className="col w aC" style={{ padding: "1rem" }}>
                <Card sx={{ minWidth: 275 }}>
                  <CardHeader
                    titleTypographyProps={{ variant: "subtitle2" }}
                    title="Valor identificado pela máquina"
                    subheader={quantificadorMaquina}
                  />
                </Card>

                <Card sx={{ minWidth: 275, marginTop: 2 }}>
                  <CardHeader
                    titleTypographyProps={{ variant: "subtitle2" }}
                    title="Valor identificado pelo homem"
                    subheader={quantificadorHomem}
                    action={
                      <Tooltip title="Excluir valor" arrow>
                        <IconButton onClick={handleRemoveValueOccurrence}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                </Card>
              </D>
            )}

            {qualificador === "Nome de Embarcação" ? (
              <CustomSelect
                label="Valor Substituto"
                margin="normal"
                value={quantificador}
                options={embarcacoes}
                onChange={(v) => setQuantificador(v)}
              />
            ) : qualificador === "Grupo" ? (
              <CustomSelect
                label="Valor Substituto"
                margin="normal"
                value={quantificador}
                options={grupos}
                onChange={(v) => setQuantificador(v)}
              />
            ) : (
              <Input
                id="input-edit-occurrency"
                type="text"
                label="Valor Substituto"
                margin="normal"
                variant="outlined"
                value={quantificador}
                onChange={(v) => setQuantificador(v)}
              />
            )}
            
          </>
        )}
        <D h="1rem" />
      </ConfirmDialog>
    </>
  );
}

// ====================================================
// COMPONENTE QUE APRESENTA A LISTA DE ACESSOS A UM DOC
// ====================================================
function MediatecaAccess({ api, docId, dialogClose }) {
  const [lists, setLists] = useState([]);
  const [openKeys, setOpenKeys] = useState({});

  useEffect(() => {
    api.get("/mediateca/docaccess/" + docId).then((r) => setLists(r));
  }, [api, docId]);

  const handleClick = (key) => () => {
    setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <MessageDialog title={`Lista de acessos ao documento ${docId}`} onOk={dialogClose}>
      <D h="1rem" />
      <List component="div">
        {lists.map(({ key, label, items }) => {
          const isOpen = openKeys[key] || false;
          return (
            <D key={key}>
              <ListItem button onClick={handleClick(key)}>
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <ListItemText inset primary={label} />
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {items.map(({ key: childKey, label: childLabel }) => (
                    <ListItem key={childKey} button sx={{ pl: 4 }}>
                      <ListItemIcon>
                        <DateRangeOutlined />
                      </ListItemIcon>
                      <ListItemText inset primary={childLabel} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
              <Divider />
            </D>
          );
        })}
      </List>
      <D h="1rem" />
    </MessageDialog>
  );
}

// ====================================================
// COMPONENTE QUE APRESENTA O PAINEL MAIS INFO DE UM DOC
// ====================================================
export function MediatecaInfo({
  file,
  facts,
  embarcacoes,
  grupos,
  closeInfo,
  dialog,
  dialogClose,
  api,
  alert,
  updateMediateca,
  permission,
  role,
}) {
  const [ocs, setOcs] = useState(null);
  const isRead = Number(file.rd_ctd) !== 0;

  const isAdmin = role === "admin";
  const updateOcs = () => {
    api.get("/mediateca/ocorrencias/" + file.doc_id).then((r) => setOcs(r));
  };

  useEffect(() => {
    updateOcs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusConfig = {
    "processado": { title: "Processado", icon: <CheckCircleOutline color="success" /> },
    "em processamento": { title: "Em processamento", icon: <Sync color="warning" /> },
    "não processado": { title: "Não processado", icon: <SyncDisabledOutlined color="action" /> },
    "erro": { title: "Com erro", icon: <DangerousOutlined color="error" /> },
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        marginLeft: "1rem",
        padding: "8px",
        background: "#f2f6fc",
        width: 300,
        gap: 1,
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* THUMBNAIL + BOTOES */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ gap: 1.2 }}>
        <Tooltip title="Abrir painel de leitura" arrow>
          <Box
            component="img"
            src={C.baseURL + "pic/" + file.thumb_png}
            alt="Thumbnail"
            sx={{
              height: 125,
              minWidth: 250,
              maxWidth: 250,
              objectFit: "contain",
              cursor: "pointer",
              display: "block",
            }}
            onClick={() => openLink(`/viewer.html?p=${file.doc_id}`)}

            /*  ==== TRECHO QUE IDENTIFICA MIME E ABRE ANTIGO VIEWER OU BROWSER DIRETO 
            onClick={() => {
              const mime = `${this.props.file.mime}`.toLowerCase();
              if (mime === 'application/pdf') {
                Abre no viewer do sistema (nova aba)
                window.open(`${C.baseURL}pdf/${this.props.file.doc_id}`, '_blank');
              } else {
                Abre direto o arquivo no navegador (nova aba)
                openLink(`/viewer.html?p=${this.props.file.doc_id}`);
              }
            }}
            */
          />
        </Tooltip>

        <Box display="flex" flexDirection="column" alignItems="flex-end" sx={{ gap: 1 }}>
          <Tooltip title="Fechar detalhes" arrow>
            <Close className="hv" color="action" onClick={closeInfo} />
          </Tooltip>

          {permission === "w" && (
            <>
              <Tooltip title="Editar fatos/ocorrências" arrow>
                <Edit
                  className="hv"
                  color="action"
                  onClick={() =>
                    dialog(
                      <MediatecaEdit
                        key={file.doc_id}
                        docId={file.doc_id}
                        facts={facts}
                        embarcacoes={embarcacoes} 
                        grupos={grupos}
                        ocs={ocs}
                        dialogClose={dialogClose}
                        api={api}
                        alert={alert}
                        updateOcs={updateOcs}
                        updateMediateca={updateMediateca}
                      />,
                    )
                  }
                />
              </Tooltip>

              <Tooltip title="Pedido de Reparo" arrow>
                <Build
                  className="hv"
                  color="action"
                  onClick={() =>
                    dialog(
                      <MediatecaEdit
                        key={file.doc_id}
                        docId={file.doc_id}
                        facts={facts}
                        embarcacoes={embarcacoes} 
                        grupos={grupos}
                        ocs={ocs}
                        dialogClose={dialogClose}
                        api={api}
                        alert={alert}
                        updateOcs={updateOcs}
                        updateMediateca={updateMediateca}
                        initialToolSelected="new"
                        initialQualificador="Pedido de Reparo"
                        initialQuantificador=""
                      />,
                    )
                  }
                />
              </Tooltip>
              
              <Tooltip title="Ordem de Manutenção" arrow>
                <Engineering
                  className="hv"
                  color="action"
                  onClick={() =>
                    dialog(
                      <MediatecaEdit
                        key={file.doc_id}
                        docId={file.doc_id}
                        facts={facts}
                        embarcacoes={embarcacoes} 
                        grupos={grupos}
                        ocs={ocs}
                        dialogClose={dialogClose}
                        api={api}
                        alert={alert}
                        updateOcs={updateOcs}
                        updateMediateca={updateMediateca}
                        initialToolSelected="new"
                        initialQualificador="Ordem de Manutenção"
                        initialQuantificador=""
                      />,
                    )
                  }
                />
              </Tooltip>
            </>
          )}

          <Tooltip title={isRead ? "Acessos ao documento" : "Documento não lido"} arrow>
            <SensorOccupied
              className={isRead ? "hv" : ""}
              color="action"
              style={{ opacity: isRead ? 1 : 0.5, cursor: isRead ? "pointer" : "default" }}
              onClick={() => {
                if (isRead)
                  dialog(<MediatecaAccess docId={file.doc_id} dialogClose={dialogClose} api={api} alert={alert} />);
              }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* PAINEL + INFO DO DOCUMENTO */}
      <Box display="flex" sx={{ gap: 1, width: "100%" }}>
        
        {/* STATUS DO ARQUIVO (Otimizado com dicionário) */}
        <Box display="flex" flexDirection="column" justifyContent="center" sx={{ width: 20 }}>
          {statusConfig[file.st] && (
            <Tooltip title={statusConfig[file.st].title} arrow>
              {statusConfig[file.st].icon}
            </Tooltip>
          )}
        </Box>

        <Box display="flex" flexDirection="column" sx={{ flexGrow: 1, minWidth: 0 }}>
          <Tooltip title={file.nome} arrow>
            <Typography
              variant="caption"
              component="div"
              sx={{
                flexGrow: 1, 
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                fontWeight: !isRead ? "bold" : "normal",
                cursor: "default",
              }}
            >
              {file.nome}
            </Typography>
          </Tooltip>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary" component="div">
              {moment(file.created_at).format("LL")}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {Math.round(Number(file.tamanho) / 1024)} kb
            </Typography>
          </Box>

          {isAdmin && (
            <span>
              <Typography variant="caption" color="text.secondary" component="div">
                Empresa: {file.empresa}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                Departamento: {file.depto}
              </Typography>
            </span>
          )}
        </Box>
      </Box>

      {/* OCORRENCIAS */}
      <Box sx={{ borderRadius: 1, background: "#fff", padding: "8px", flexGrow: 1, minHeight: 0, overflowY: "auto" }}>
        <MediatecaQualificators key={file.doc_id} ocs={ocs} />
      </Box>
    </Box>
  );
}


// ====================================================
// COMPONENTE QUE RENDERIZA UM DOC COMO CARD 
// ====================================================
const MediatecaCard = ({ file, isSelected, onClick, getInfo }) => {
  const { doc_id, tipodoc, nome, fatoprincipal, grupo, data_aprox, st, thumb_png, rd_ctd, editado } = file;
  const isRead = Number(rd_ctd) === 0;
  const isEdt = Number(editado) === 1;

  return (
    <Card
      component={D}
      className="col aF"
      style={{
        minWidth: "200px",
        maxWidth: "200px",
        maxHeight: "170px",
        margin: "0.3rem",
        border: isSelected ? "2px solid #3217d1" : "none",
      }}
      onClick={() => {
        onClick(doc_id);
        getInfo(doc_id);
      }}
    >
      {thumb_png ? (
        <D e
          h="70px"
          w="100%"
          img={C.baseURL + "pic/" + thumb_png}
          style={{ backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }}
        />
      ) : (
        <Skeleton animation="wave" height={70} variant="rectangular" />
      )}

      <CardContent className="col e aF" component={D}>
        <Typography variant="caption" component="div" className="th" sx={{ fontWeight: isRead ? "bold" : "normal" }}>
          {tipodoc || nome}
        </Typography>
        <D className="row w aB">
          <D className="col e aF">
            <Typography variant="caption" component="div" sx={{ fontWeight: isRead ? "bold" : "normal" }}>
              <div>{fatoprincipal || "---"}</div>
              <div>{!grupo || grupo === "Indeterminado" ? "---" : grupo}</div>
              <div>{data_aprox ? moment(data_aprox).format("DD/MM/YYYY") : "---"}</div>
            </Typography>
          </D>
          {st === "processado" && <CheckCircleOutline sx={{ color: isRead ? "success.dark" : "success.main" }} />}
          {st === "em processamento" && <Sync color="warning" />}
          {st === "não processado" && <SyncDisabledOutlined color="action" />}
          {st === "erro" && <DangerousOutlined color="error" />}
          {isEdt && <Edit color="warning" />}
        </D>
      </CardContent>
    </Card>
  );
};

// ====================================================
// COMPONENTE QUE RENDERIZA A MEDIATECA COMO GRID DE CARDS
// ====================================================
const MediatecaGrid = ({ fls, searchField, getInfo, onClick, selectedRows }) =>
  fls && typeof fls === "object" ? (
    <>
      <Divider />
      <D className="col e aF scB" style={{ paddingTop: "1rem", paddingBottom: "3rem" }}>
        <D className="wrp">
          {fls.map((f) =>
            checker(f.ss, searchField) ? (
              <MediatecaCard
                key={f.thumb_png}
                file={f}
                getInfo={getInfo}
                onClick={onClick}
                isSelected={selectedRows.includes(f.doc_id)}
              />
            ) : null,
          )}
        </D>
      </D>
      <Divider />
    </>
  ) : null;

// Componente funcional externo para fazer truncagem de texto longo limitando a 20rem
// usado para apresentar o nome do arquivo com reticencias qdo longo
const NomeCell = ({ nome, isBold }) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
    }
  }, [nome]);

  const content = (
    <div
      ref={ref}
      style={{
        maxWidth: "20rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontWeight: isBold ? "bold" : "normal",
      }}
    >
      {nome}
    </div>
  );

  return isTruncated ? (
    <Tooltip title={nome} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

// Componente CheckBox generico para evitar lag no click
export const FastCheckbox = ({ isChecked, onToggle }) => {
  // Cria um estado local para responder ao clique imediatamente
  const [localChecked, setLocalChecked] = useState(isChecked);

  // Guarda o valor anterior da prop para comparação
  const [prevIsChecked, setPrevIsChecked] = useState(isChecked);

  // 3. Se o pai alterou a prop (ex: limpou os filtros), ajustamos o estado durante o render
  if (isChecked !== prevIsChecked) {
    setPrevIsChecked(isChecked);
    setLocalChecked(isChecked);
  }
  
  const handleChange = (e) => {
    // Marca visualmente na hora (sem lag)
    setLocalChecked(e.target.checked);

    // 2. Avisa o pai com um micro-atraso, permitindo que a animação termine fluida
    setTimeout(() => {
      onToggle();
    }, 50);
  };

  // Usamos 'checked' e não 'defaultChecked' para manter o controle
  return <Checkbox checked={localChecked} onChange={handleChange} />;
};

// cabeçalho das colunas da mediateca como lista
// tambem usado no back para indicar a coluna de ordenacao
const headCells = [
  { id: "ID", label: "ID" },
  { id: "Status", label: "Status" },
  { id: "Tipo", label: "Tipo de documento" },
  { id: "Data Aproximada", label: "Data Aproximada" },  
  { id: "Embarcacao", label: "Embarcação" },
  { id: "Grupo", label: "Filial-Grupo" },
  { id: "Nome", label: "Nome do arquivo" },
  { id: "Criado", label: "Criado em" },
];


// ====================================================
// COMPONENTE QUE APRESENTA A MEDIATECA COMO LISTA DE DOCS
// ====================================================
const MediatecaList = memo(
  ({ fls, selectedRows = [], onUnselectAll, orderBy, order, updateOrder_OrderBy, isLoading, onRowClick, getInfo }) => {
    const createSortHandler = (property) => () => {
      const isAsc = orderBy === property && order === "asc";
      updateOrder_OrderBy(isAsc ? "desc" : "asc", property);
    };

    return (
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer style={{ height: "100%" }}>
          <Table stickyHeader size="small">
            {/* CABEÇALHO DA TABELA */}
            <TableHead>
              <TRow sx={{ height: "3rem", background: "#ccc" }}>
                <TCell sx={{ padding: "0 2px", width: "32px", textAlign: "center" }}>
                  {selectedRows.length > 0 && (
                    <Tooltip title="Desmarcar documentos" arrow>
                      <IconButton
                        size="small"
                        onClick={onUnselectAll}
                        sx={{ color: "#f8f4f4", width: 18, height: 20, }}
                      >
                        <Unpublished sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TCell>

                {/* place holder do botão de info */}
                <TCell sx={{ padding: "0", width: "32px" }} /> {/* <-- Ajuste de padding e largura fixa */}

                {headCells.map((headCell) => (
                  <TCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    // Se for a coluna de ID, reduz o padding lateral para aproximar da coluna de Info
                    sx={{
                      // Mantém a sua largura reduzida para o ID
                      ...(headCell.id === "ID" && { paddingLeft: "8px", width: "30px" }),
                      // Iguala o padding do cabeçalho de Status com o do corpo
                      ...(headCell.id === "Status" && { paddingLeft: "16px", paddingRight: "4px", width: "70px" }),
                      // Opcional: tirando um pouco do padding esquerdo do "Tipo" para aproximar ainda mais
                      ...(headCell.id === "Tipo" && { paddingLeft: "0px" }),
                    }}
                  >
                    <TSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={createSortHandler(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TSortLabel>
                  </TCell>
                ))}
              </TRow>
            </TableHead>

            {/* CORPO DA TABELA */}
            <TableBody>
              {/* Renderiza as linhas quando existem dados e não está carregando */}
              {Array.isArray(fls) &&
                !isLoading &&
                fls.length > 0 &&
                fls.map((f) => (
                  <TRow key={f.thumb_png} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TCell sx={{ padding: "0 2px", width: "32px", textAlign: "center" }}>
                      {/* <Tooltip title="Selecionar" arrow> */}
                        <span style={{ 
                          display: "inline-block", // Retornamos pro inline-block
                          lineHeight: 0,           // MATA o espaço extra acima e abaixo
                          fontSize: 0,             // MATA qualquer resquício de texto fantasma
                          verticalAlign: "middle"  // Alinha perfeitamente com a tabela
                        }}>
                          <FastCheckbox 
                            isChecked={selectedRows.includes(f.doc_id)} 
                            onToggle={() => onRowClick(f.doc_id)}
                          />
                        </span>
                      {/* </Tooltip> */}
                    </TCell>

                    <TCell sx={{ padding: "0", width: "32px", textAlign: "center" }}>
                      <Tooltip title="Detalhes" arrow>
                        <IconButton onClick={() => getInfo(f.doc_id)}>
                          <InfoOutlined />
                        </IconButton>
                      </Tooltip>
                    </TCell>

                    <TCell component="th" sx={{ paddingLeft: "8px", paddingRight: "8px", width: "60px" }}>
                      <Tooltip title="Detalhes" arrow>
                        {/* Reduzindo a folga antes e depois do ID */}
                        <D className="th" 
                          style={{ 
                            fontWeight: f.rd_ctd === "0" ? "bold" : "normal",
                            cursor: "pointer",        // Adiciona a mãozinha ao passar o mouse
                            display: "inline-flex",   // <-- inline-flex não força a quebra de bloco
                            alignItems: "center",     // <-- centraliza o texto no próprio eixo
                            verticalAlign: "middle"   // <-- alinha com a linha de base do IconButton ao lado
                          }}
                          onClick={() => getInfo(f.doc_id)}
                        >
                          {f.doc_id}
                        </D>
                      </Tooltip>
                    </TCell>

                    {/* CAMPO STATUS */}
                    <TCell component="th" sx={{ paddingLeft: "8px", paddingRight: "4px", width: "70px" }}>
                      {/* Box flex para forçar o alinhamento vertical e dar um respiro entre os ícones */}
                      <Box display="flex" alignItems="center" gap={0.0}>

                        {/* ÍCONES DE STATUS */}
                        {f.st === "processado" && (
                          <Tooltip title="Processado" arrow>
                            <CheckCircleOutline sx={{ color: f.rd_ctd === "0" ? "success.dark" : "success.main" }} />
                          </Tooltip>
                        )}
                        {f.st === "em processamento" && (
                          <Tooltip title="Em processamento" arrow>
                            <Sync color="warning" />
                          </Tooltip>
                        )}
                        {f.st === "não processado" && (
                          <Tooltip title="Não processado" arrow>
                            <SyncDisabledOutlined color="action" />
                          </Tooltip>
                        )}
                        {f.st === "erro" && (
                          <Tooltip title="Com erro" arrow>
                            <DangerousOutlined color="error" />
                          </Tooltip>
                        )}

                        {/* ÍCONE DE PENDENCIAS */}
                        {f.pend === 1 && (
                          <Tooltip title="C/Pendências" arrow>
                            <IconButton
                              size="small"
                              onClick={() => getInfo(f.doc_id)}
                              sx={{ padding: 0.2 }} // Reduz o padding para não inchar a altura da linha da tabela
                            >
                              <PendingActions color="warning" sx={{ fontSize: "22px" }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* ÍCONE DE EDITADO */}
                        {f.editado && (
                          <Tooltip title="Editado" arrow>
                            <IconButton
                              size="small"
                              onClick={() => getInfo(f.doc_id)}
                              sx={{ padding: 0.2 }} // Reduz o padding para não inchar a altura da linha da tabela
                            >
                              <Edit color="warning" sx={{ fontSize: "20px" }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* ÍCONE DE PR_OM */}
                        {f.pr_om === 1 && (
                          <Tooltip title="PR ou OM" arrow>
                            <IconButton
                              size="small"
                              onClick={() => getInfo(f.doc_id)}
                              sx={{ padding: 0.2 }} // Reduz o padding para não inchar a altura da linha da tabela
                            >
                              <Build color="warning" sx={{ fontSize: "20px", transform: 'scaleX(-1)' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TCell>

                    <TCell component="th" sx={{ paddingLeft: "0px" }}>
                      <D className="th" style={{ maxWidth: "20rem", fontWeight: f.rd_ctd === "0" ? "bold" : "normal" }}>
                        {f.tipodoc}
                      </D>
                    </TCell>

                    <TCell component="th" style={{ fontWeight: f.rd_ctd === "0" ? "bold" : "normal" }}>
                      {moment(f.dtaprox, "DD/MM/YYYY").format("DD/MM/YYYY")}
                    </TCell>

                    {/* Embarcação */}
                    <TCell component="th">
                      <D className="th" style={{ maxWidth: "20rem", fontWeight: f.rd_ctd === "0" ? "bold" : "normal" }}>
                        {f.fatoprincipal}
                      </D>
                    </TCell>

                    <TCell component="th">
                      <D className="th" style={{ maxWidth: "20rem", fontWeight: f.rd_ctd === "0" ? "bold" : "normal" }}>
                        {f.grupo}
                      </D>
                    </TCell>

                    <TCell component="th">
                      <NomeCell nome={f.nome} isBold={f.rd_ctd === "0"} />
                    </TCell>

                    <TCell component="th" style={{ fontWeight: f.rd_ctd === "0" ? "bold" : "normal" }}>
                      {moment(f.created_at).format("DD/MM/YYYY")}
                    </TCell>
                  </TRow>
                ))}

              {/* Aviso de lista vazia (já carregou e não achou nada) */}
              {(!fls || fls.length === 0) && !isLoading && (
                // {Math.random().toString()}>
                <TRow key="0">
                  <TCell component="th" align="center" colSpan={11}>
                    Nenhum documento encontrado
                  </TCell>
                </TRow>
              )}

              {/* Spinner de carregamento */}
              {(!fls || isLoading) && (
                <TRow key="-1">
                  <TCell component="th" align="center" colSpan={11}>
                    <CircularProgress />
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  },
);


// ====================================================
// COMPONENTE PRINCIPAL DA MEDIATECA
// ====================================================
export const Mediateca = (props) => {
  // 1. SINCRONIZAÇÃO DE ESTADO
  // Mantemos um estado centralizado (como na classe) e um Ref sincronizado.
  const [state, _setState] = useState({
    aproxIniDateMenu: null,
    aproxFimDateMenu: null,
    attWordMenu: null,
    groupsMenu: null,
    uploadExpanded: true,
    selectedRows: [],
    searchField: "",
    searchResetKey: 0,
    hasSearchDraft: false,
    visibleUploads: [],
    viewType: "list",
    infoView: null,
    fls: null,
/*
        aprovacao: null
        arquivo_id: 20374
        court_id: 1
        created_at: "2026-07-23T18:43:44.648Z"
        data_aprox: null
        data_hora_upload: "2026-07-23T18:43:44.648Z"
        depto: "Suporte AI2"
        doc_id: 20374
        documento_id: 20374
        dtaprox: null
        editado: true
        empresa: "AI2"
        fatoprincipal: null
        grupo: null
        id: 20374
        lido: false
        login_upload: 98
        mime: "image/jpeg"
        nome: "impeto-prog.jpg"
        npages: null
        palavra_de_atencao: null
        pend: 0
        pr_om: 1
        rd_ctd: "0"
        rn: "1"
        searchingid: 20374
        st: "erro"
        status: "erro"
        subtipo: "jpg"
        tamanho: 451786
        thumb_png: "87qkcmeoel0ut4y58kfajc"
        tipodoc: null
        updated_at: "2026-07-23T18:43:44.648Z"
        vara_id: 1
*/
    attWords: [],
    selectedAttWords: [],
    facts: [],
    selectedAproxIniDate: "",
    selectedAproxFimDate: "",
    order: "desc",
    orderBy: "Data Aproximada",
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    count: 0,
    data: [],
    groups: [],
    gruposInfo: [],
    selectGroups: [],
    selectCurrentAtt: [],
    embarcacoes: [],
    selectEmbarcacoes: [],
    extensionsDocuments: [],
    groupsTypeDocuments: null,
    selectTypeDocuments: [],
    tiposDocumentos: [],
    selectTiposDocumentos: [],
    tiposDocumentosMenu: null,

    pendencias: 0,
    selected: 0,
    unread: false,
    filtersPend: false,
    pr_om: 0,
    edt: 0,
    
    isLoading: false,
    showLargeMediatecaWarning: false,
    isExporting: false,
  });

  const inputFilesRef = useRef(null); // Adicionado para substituir o inputfiles
  const stateRef = useRef(state);

  // 2. VARIÁVEIS DE INSTÂNCIA (Antigos "this.variavel")
  const searchDraft = useRef("");
  // const searchFieldResetKey = useRef(0);
  const pipe = useRef([]);
  const interval = useRef(null);
  const mounted = useRef(false);
  const logUser = useRef(true);
  const isReady = useRef(false);
  const prevPagination = useRef({ page: 1, pageSize: DEFAULT_PAGE_SIZE, order: "desc", orderBy: "Data Aproximada" });
  const isProcessing = useRef(false); // indica estado de processamento da fila de upload

  // Função utilitária que simula o `this.setState` da classe perfeitamente
  const updateState = useCallback((updater) => {
    let nextState;
    if (typeof updater === "function") {
      nextState = { ...stateRef.current, ...updater(stateRef.current) };
    } else {
      nextState = { ...stateRef.current, ...updater };
    }
    stateRef.current = nextState; // Atualiza o Ref de forma síncrona
    _setState(nextState); // Engatilha o render de forma assíncrona
  }, []);

  // 3. FUNÇÕES PRINCIPAIS
  const computeHasFilters = (s) => {
    const {
      searchField,
      selectedAproxIniDate,
      selectedAproxFimDate,
      selectTiposDocumentos,
      selectedAttWords,
      selectEmbarcacoes,
      selectGroups,
      selectTypeDocuments,
      hasSearchDraft, 
    } = s;

    return (
      hasSearchDraft || // tem texto digitado
      (searchField && searchField.trim() !== "") ||
      !!selectedAproxIniDate ||
      !!selectedAproxFimDate ||
      selectTiposDocumentos?.length > 0 ||
      selectedAttWords?.length > 0 ||
      selectEmbarcacoes?.length > 0 ||
      selectGroups?.length > 0 ||
      selectTypeDocuments?.length > 0
    );
  };

  const prepareSearchValues = () => {
    const currentSearch = stateRef.current.searchField;
    if (currentSearch.length > 0) {
      let searchValues = [currentSearch.trim()];
      searchValues = searchValues[0].split(" ");
      return JSON.stringify(searchValues);
    }
    return "[]";
  };

  const buildMediatecaParams = () => {
    const s = stateRef.current;
    const paramsObject = {
      page: s.page,
      pageSize: s.pageSize,
      dataInicio: s.selectedAproxIniDate,
      dataFim: s.selectedAproxFimDate,
      palavrasDeAtencao: JSON.stringify(s.selectedAttWords),
      searching: prepareSearchValues(),
      orderBy: JSON.stringify([s.orderBy, s.order]),
      embarcacoes: JSON.stringify(s.selectEmbarcacoes),
      grupo: JSON.stringify(s.selectGroups),
      typeDocuments: JSON.stringify(s.selectTypeDocuments),
      tiposDocumentos: JSON.stringify(s.selectTiposDocumentos),
      pends: s.pendencias,
      unread: s.unread,
      selected: s.selected,
      selectedDocs: s.selected === 1 ? JSON.stringify(s.selectedRows) : "[]",
      pr_om: s.pr_om,
      edt: s.edt,
    };
    return new URLSearchParams(paramsObject).toString();
  };


  const getMediatecaPayload = async () => {
    updateState({ isLoading: true });
    const s = stateRef.current;

    // Salva todas as preferências no banco com uma única chamada (inclui grupos e datas)
    await saveUserPreferences(s);

    // Monta os parâmetros para a busca real da Mediateca
    const urlSearchParams = buildMediatecaParams();
    const shouldLogUser = logUser.current;
    logUser.current = false;

    let payload;
    if (shouldLogUser) {
      payload = await props.api.get(`mediateca/todos?${urlSearchParams}`);
    } else {
      payload = await props.api.get(`mediateca/allsilent?${urlSearchParams}`);
    }

    updateState({ isInfoOpen: false, count: payload.count, fls: payload.data, isLoading: false });
  };

  const executarBusca = () => {
    updateState((prev) => {
      return { ...prev, searchField: searchDraft.current, page: 1, filtersPend: false };
    });
    // Chamamos o fetch imediatamente. Como usamos stateRef lá dentro,
    // ele já enxerga o 'page: 1' e os novos filtros setados acima de forma síncrona.
    getMediatecaPayload();
  };

  // === NOVA FUNÇÃO CENTRALIZADA DE SALVAMENTO DE PREFERENCIAS ===
  const saveUserPreferences = async (s) => {
    // Busca os IDs dos grupos. O ?. evita exceções.
    const gruposIds = s.selectGroups
      .map((nome) => stateRef.current.gruposInfo?.find((val) => val.nome === nome)?.id)
      .filter(Boolean);

    const dateParams = {
      dataInicio: s.selectedAproxIniDate ? moment(s.selectedAproxIniDate, "YYYY-MM-DD").format("DD/MM/YYYY") : "",
      dataFim: s.selectedAproxFimDate ? moment(s.selectedAproxFimDate, "YYYY-MM-DD").format("DD/MM/YYYY") : ""
    };

    // Montamos o objeto APENAS com o que a Mediateca governa.
    const fullPreferences = {
      user_id: props.user.id,
      
      selectAttword: s.selectedAttWords,
      selectGroups: s.selectGroups,
      selectEmbarcacoes: s.selectEmbarcacoes,
      selectGroupsId: gruposIds, // Se for [], o Postgres vai salvar [], o que é correto SE o usuário limpou o filtro.
      selectTypeDocuments: s.selectTypeDocuments,
      selectTiposDocumentos: s.selectTiposDocumentos,
      selectDateParameters: dateParams,
      
      pendencias: Number(s.pendencias) || 0,
      pr_om: Number(s.pr_om) || 0,
      unread: Boolean(s.unread) || false,
      edt: Number(s.edt) || 0,
      docspage: Number(s.pageSize) || DEFAULT_PAGE_SIZE,
    };

    await props.api.post("/user/preferences", fullPreferences).catch(console.error);
  };


  const cleanFilter = async () => {
    searchDraft.current = "";

    updateState((prev) => ({
      selectedAttWords: [],
      selectGroups: [],
      selectEmbarcacoes: [],
      selectTypeDocuments: [],
      selectTiposDocumentos: [],
      selectedAproxIniDate: "",
      selectedAproxFimDate: "",
      searchResetKey: (prev.searchResetKey || 0) + 1, // Força uma nova key única
      searchField: "",
      hasSearchDraft: false,
      page: 1,
      pendencias: 0,
      selected: 0,
      unread: false,
      filtersPend: false,
      pr_om: 0,
      edt: 0,
    }));

    // await changeSelectAttA();
    getMediatecaPayload();
  };

  // LÓGICA DE UPLOAD DE DOCUMENTOS
  const sendFile = async (i) => {
    const item = pipe.current[i];
    if (!item || !item.file) return;

    const data = new FormData();
    data.append("file", item.file, item.file.name);
    try {
      const res = await props.api.post("/mediateca/upload", data);
      if (res.status && res.status !== "success") {
        item.status = "Error";
      } else {
        item.status = "Done";
      }
    } catch {
      item.status = "Error";
    } finally {
      item.createdAt = new Date();
      updatePipe();
    }
  };

  const updatePipe = () => {
    // Limpeza dos itens expirados (15 segundos)
    const visibleFiles = pipe.current.filter((item) => {
      // Se não tem createdAt (ou seja, não começou a subir ainda), mantém na tela
      if (!item.createdAt) return true;
      
      // Verifica se passaram 15 segundos desde que o status mudou para Done/Error
      const expired = moment().isAfter(moment(item.createdAt).add(15, "seconds"));
      return !(expired && (item.status === "Done" || item.status === "Error"));
    });

    // CORREÇÃO DE MEMORY LEAK: Atualizamos o pipe real para jogar fora o histórico expirado
    pipe.current = visibleFiles;
    
    // Atualizamos o estado visual (o modal fecha sozinho se o array ficar vazio)
    updateState({ visibleUploads: visibleFiles });

    // Conta quantos arquivos ainda precisam ser processados
    const pendingItems = pipe.current.filter((item) => item.status === "Pending");
    const uploadingCount = pipe.current.filter((item) => item.status === "Uploading").length;

    const hasWork = pendingItems.length > 0 || uploadingCount > 0;

    if (hasWork) {
      isProcessing.current = true; // Marca que a máquina está trabalhando
    } else if (isProcessing.current) {
      // Se acabou o trabalho e a máquina estava ligada, terminamos a bateria de uploads!
      isProcessing.current = false;
      executarBusca(); // Atualiza o grid da Mediateca
      
      // Assim que o último arquivo termina, agendamos uma varredura para exatamente 10s depois
      setTimeout(() => {
        updatePipe();
      }, 10000);
    }

    // Envia o que faltar respeitando o limite de concorrência
    const availableSlots = Math.max(MAX_CONCURRENT_UPLOADS - uploadingCount, 0);

    const itemsToUpload = pendingItems.slice(0, availableSlots);
    itemsToUpload.forEach((item) => {
      item.status = "Uploading";
      const index = pipe.current.indexOf(item);
      sendFile(index);
    });
  };

  const uploadFiles = () => {
    const input = inputFilesRef.current;
    if (!input || !input.files) return;

    const fls = Array.from(input.files);
    fls.forEach((f) => pipe.current.push({ file: f, status: "Pending", createdAt: new Date() }));
    pipe.current = _.sortBy(pipe.current, "createdAt");
    updatePipe();
    input.value = "";
  };

  const changeTimeInterval = (t) => {
    clearInterval(interval.current);
    interval.current = setInterval(() => updatePipe(), t * 1000);
  };


  // INTERAÇÕES DE GRID/MODAL/TABELA

  const getInfo = (i) => updateState({ selectedRows: [i], isInfoOpen: true });

  const closeInfo = () =>
    updateState((prev) => ({
      isInfoOpen: false,
      selectedRows: prev.viewType === "list" ? prev.selectedRows : [],
    }));

  const viewAlertDoc = () => {
    const alertdoc = new URLSearchParams(window.location.search).get("alertdoc");
    if (alertdoc) {
      props.api.get("/mediateca/ocorrencias/" + alertdoc).then(() => true);
      openLink(`/viewer.html?p=${alertdoc}`);
    }
  };


const exportToExcel = async ({ title = "Mediateca" }) => {
    const s = stateRef.current;
    if (s.count === 0) return;
    
    updateState({ isExporting: true });
    
    try {
      // Busca de Dados
      const urlSearchParams = buildMediatecaParams();
      const r = await props.api.get(`mediateca/excel?${urlSearchParams}`);
      const files = r.data;
      if (!files?.length) return;

      // Preparação dos Filtros
      const parts = [];
      if (s.unread) parts.push("Não lidos");
      if (s.pendencias) parts.push("Com pendências");
      if (s.pr_om) parts.push("Com PR ou OM");
      if (!!s.edt) parts.push("Editados");
      if (s.selected) parts.push("Selecionados");
      
      if (s.selectedAproxIniDate && s.selectedAproxFimDate) {
        parts.push(`Entre ${moment(s.selectedAproxIniDate).format("DD/MM/YYYY")} e ${moment(s.selectedAproxFimDate).format("DD/MM/YYYY")}`);
      } else if (s.selectedAproxIniDate) {
        parts.push(`De ${moment(s.selectedAproxIniDate).format("DD/MM/YYYY")}`);
      } else if (s.selectedAproxFimDate) {
        parts.push(`Até ${moment(s.selectedAproxFimDate).format("DD/MM/YYYY")}`);
      }

      // Mapeamento de Dados
      const data = files.map((fl) => {
        const partesStatus = [];
        
        if (fl.st === "processado") partesStatus.push("✅");
        else if (fl.st === "em processamento") partesStatus.push("🔃");
        else if (fl.st === "não processado") partesStatus.push("⏳");
        else if (fl.st === "erro") partesStatus.push("❌");
        
        if (fl.rd_ctd === "0") partesStatus.push("✉️");
        if (fl.pend === 1) partesStatus.push("⚠️");
        if (fl.pr_om === 1) partesStatus.push("🔧");
        if (fl.editado) partesStatus.push("✏️");

        return [
          fl.doc_id,
          // fl.st,
          partesStatus.join(" "),
          fl.tipodoc,
          fl.dtaprox ? fl.dtaprox : "Data inválida",
          fl.fatoprincipal,
          fl.grupo,
          fl.nome,
          fl.created_at ? new Date(fl.created_at) : null,
          { t: "s", v: " Abrir documento", l: { Target: `${window.origin}/viewer.html?p=${fl.doc_id}` } }
        ]
      }
    );

      // Chamada da Função Genérica com formatação customizada
      exportStandardExcel({
        title: title,
        filtersText: parts.length ? parts.join(", ") : "",
        columns: [
          { header: 'ID', width: 6 },
          { header: 'Status', width: 15 },
          { header: 'Tipo do Documento', width: 22 },
          { header: 'Data Aproximada', width: 15 },
          { header: 'Embarcação', width: 15 },
          { header: 'Filial-Grupo', width: 20 },
          { header: 'Nome do Arquivo', width: 40 },
          { header: 'Criado em', width: 20 },
          { header: 'Link para o documento', width: 25 }
        ],
        data: data,
        fileNamePrefix: "Mediateca",
        customFormatting: (worksheet, mappedData) => {
          mappedData.forEach((_, idx) => {
            const cell = worksheet[`H${5 + idx}`];
            if (cell) cell.z = "dd/mm/yyyy hh:mm:ss";
          });
        }
      });

    } finally {
      updateState({ isExporting: false });
    }
  };


// ====================================================
// EFEITOS DE CICLO DE VIDA
// ====================================================

  // Inicialização (Mount)
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    changeTimeInterval(30);

    (async () => {
      // Dados auxiliares disparados em paralelo
      props.api.get("/AttWords").then((r) => updateState({ attWords: _.pluck(r, "texto") }));
      props.api.get("/Facts").then((r) => updateState({ facts: r }));
      props.api.get("/mediateca/extensions-documents").then((r) => updateState({ extensionsDocuments: r }));
      props.api.get("/Grupos").then((data) => updateState({ groups: data.map((g) => g.nome), gruposInfo: data }));
      props.api.get("/mediateca/tiposdoc").then((res) => updateState({ tiposDocumentos: Array.isArray(res) ? res : [] })).catch(console.error);
      props.api.get("/embarcacoesList").then((r) => updateState({ embarcacoes: r })).catch(console.error);
      // === GET ÚNICO DE TODAS AS PREFS ===
      const userPrefs = await props.api.get(`/user/preferences?user_id=${props.user.id}`);
      
      const dateFilter = { selectedAproxIniDate: "", selectedAproxFimDate: "" };
      if (userPrefs?.selectDateParameters) {
        let dateParamsObj = userPrefs.selectDateParameters;
        if (typeof dateParamsObj === 'string') {
          try { dateParamsObj = JSON.parse(dateParamsObj); } 
          catch { dateParamsObj = {}; }
        }
        if (dateParamsObj.dataInicio && dateParamsObj.dataInicio.trim() !== "") {
          dateFilter.selectedAproxIniDate = moment(dateParamsObj.dataInicio, "DD/MM/YYYY").format("YYYY-MM-DD");
        }
        if (dateParamsObj.dataFim && dateParamsObj.dataFim.trim() !== "") {
          dateFilter.selectedAproxFimDate = moment(dateParamsObj.dataFim, "DD/MM/YYYY").format("YYYY-MM-DD");
        }
      }
      
      updateState({
        ...dateFilter,
        // LÊ AS CHAVES EXATAS DO BANCO (AS MESMAS DO PAYLOAD DE POST)
        selectedAttWords: userPrefs?.selectAttword ?? [],
        selectGroups: userPrefs?.selectGroups ?? [],
        selectEmbarcacoes: userPrefs?.selectEmbarcacoes ?? [],
        selectTypeDocuments: userPrefs?.selectTypeDocuments ?? [],
        selectTiposDocumentos: userPrefs?.selectTiposDocumentos ?? [],
        
        pendencias: Number(userPrefs?.pendencias) || 0,
        unread: Boolean(userPrefs?.unread) || false,
        edt: Number(userPrefs?.edt) || 0,
        pr_om: Number(userPrefs?.pr_om) || 0,
        pageSize: Number(userPrefs?.docspage) || DEFAULT_PAGE_SIZE,
        page: 1,
      });

      // Libera reações e faz o primeiro fetch da grid
      isReady.current = true;
      getMediatecaPayload();
      viewAlertDoc();
    })(); 

    return () => clearInterval(interval.current); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Controle de Efeito da Paginação (Equivalente do componentDidUpdate)
  useEffect(() => {
    if (!isReady.current) return;

    const p = prevPagination.current;
    const paginationChanged =
      state.pageSize !== p.pageSize || state.orderBy !== p.orderBy || state.order !== p.order || state.page !== p.page;

    if (paginationChanged) {
      getMediatecaPayload();
      prevPagination.current = {
        page: state.page,
        pageSize: state.pageSize,
        order: state.order,
        orderBy: state.orderBy,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.page, state.pageSize, state.order, state.orderBy]);

  // 7. RENDER
  const hasFilters = computeHasFilters(state);  // deriva a existencia de filtros ativos

  const limit = Math.ceil(state.count / state.pageSize) || 0; // deriva o limite de paginas pro paginador

  return (
    <>
      {state.isExporting && (
        <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}>
          <CircularProgress size={64} thickness={5} sx={{ color: "primary", "& circle": { stroke: "currentColor" } }} />
        </Backdrop>
      )}

      <D className="col e aF scD" style={{ padding: "1rem 2rem", gap: "1rem" }}>
        
        {/* CONTÊINER PRINCIPAL DA LINHA (Usa space-between para separar os dois blocos) */}
        <D className="r2c w aC pa" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '1rem' }}>
          
          {/* ================================================================ */}
          {/* 1º CONJUNTO: ALINHADO À ESQUERDA (SearchField até Limpar Filtro) */}
          {/* ================================================================ */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
            
            <SearchField
              className="row w e aC"
              style={{ borderRadius: "0.5rem", marginRight: "1rem" }}
              key={`search-${state.searchResetKey || 0}`} // Recebe o incremento do cleanFilter
              defaultValue={state.searchField}
              onChange={(value) => {
                const text = value || "";
                const isNowEmpty = text.trim() === "";
                
                searchDraft.current = text;
                
                // MÁGICA: Puxamos o estado em tempo real da Ref, totalmente imune a atrasos
                const currentState = stateRef.current;
                
                // Se o Atualizar precisa acender OU se a caixa mudou de vazio p/ cheio (ou vice-versa)
                if (!currentState.filtersPend || currentState.hasSearchDraft === isNowEmpty) {
                  updateState({ 
                    filtersPend: true, 
                    hasSearchDraft: !isNowEmpty 
                  });
                }
              }}
            />
            
            <D className="row aC" style={{ background: "#eeeeee", borderRadius: "0.5rem" }}>
              {/* DATA INICIAL */}
              <Tooltip title={`Data inicial ${state.selectedAproxIniDate ? moment(state.selectedAproxIniDate).format("DD/MM/YYYY") : ""}`} arrow>
                <IconButton onClick={(e) => updateState({ aproxIniDateMenu: e.currentTarget })} color={state.selectedAproxIniDate ? "primary" : "default"}>
                  <DateRangeOutlined />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={state.aproxIniDateMenu} open={!!state.aproxIniDateMenu} onClose={() => updateState({ aproxIniDateMenu: null })}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    value={state.selectedAproxIniDate ? moment(state.selectedAproxIniDate) : null}
                    maxDate={state.selectedAproxFimDate ? moment(state.selectedAproxFimDate) : undefined}
                    renderInput={(params) => <TextField {...params} />}
                    onChange={(v) => {
                      const dateValue = v ? moment(v).format("YYYY-MM-DD") : "";
                      updateState((prev) => {
                        return {
                          ...prev,
                          selectedAproxIniDate: dateValue,
                          selectedAproxFimDate: prev.selectedAproxFimDate && moment(v).isAfter(prev.selectedAproxFimDate) ? moment(v).format("YYYY-MM-DD") : prev.selectedAproxFimDate,
                          aproxIniDateMenu: null, filtersPend: true 
                        };
                      });
                    }}
                  />
                </LocalizationProvider>
                <DialogActions>
                  <Button variant="outlined" color="primary" onClick={() => updateState((prev) => {
                      return { ...prev, selectedAproxIniDate: "", page: 1, filtersPend: true };
                    })}>
                    Limpar data
                  </Button>
                </DialogActions>
              </Menu>

              {/* DATA FINAL */}
              <Tooltip title={`Data final ${state.selectedAproxFimDate ? moment(state.selectedAproxFimDate).format("DD/MM/YYYY") : ""}`} arrow>
                <IconButton onClick={(e) => updateState({ aproxFimDateMenu: e.currentTarget })} color={state.selectedAproxFimDate ? "primary" : "default"}>
                  <DateRangeOutlined />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={state.aproxFimDateMenu} open={!!state.aproxFimDateMenu} onClose={() => updateState({ aproxFimDateMenu: null })}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    value={state.selectedAproxFimDate ? moment(state.selectedAproxFimDate) : null}
                    minDate={state.selectedAproxIniDate ? moment(state.selectedAproxIniDate) : undefined}
                    renderInput={(params) => <TextField {...params} />}
                    onChange={(v) => {
                      const dateValue = v ? moment(v).format("YYYY-MM-DD") : "";
                      updateState((prev) => {
                       return {
                          ...prev,
                          selectedAproxFimDate: dateValue,
                          selectedAproxIniDate: prev.selectedAproxIniDate && moment(v).isBefore(prev.selectedAproxIniDate) ? moment(v).format("YYYY-MM-DD") : prev.selectedAproxIniDate,
                          aproxFimDateMenu: null, filtersPend: true
                        };
                      });
                    }}
                  />
                </LocalizationProvider>
                <DialogActions>
                  <Button variant="outlined" color="primary" onClick={() => updateState((prev) => {
                      return { ...prev, selectedAproxFimDate: "", page: 1, filtersPend: true };
                    })}>
                    Limpar data
                  </Button>
                </DialogActions>
              </Menu>
            </D>

            {/* TIPO DE DOCUMENTO */}
            <Tooltip title="Tipo de Documento" arrow>
              <IconButton onClick={(e) => updateState({ tiposDocumentosMenu: e.currentTarget })} color={state.selectTiposDocumentos.length > 0 ? "primary" : "default"}>
                <Article />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={state.tiposDocumentosMenu} open={!!state.tiposDocumentosMenu} onClose={() => updateState({ tiposDocumentosMenu: null })}>
              <div style={{ maxHeight: "30vh", overflowY: "auto", display: "flex", flexDirection: "column", padding: "0.3rem 1rem 0.3rem 0.3rem" }}>
                {state.tiposDocumentos.map((doc) => {
                  const isChecked = state.selectTiposDocumentos.includes(doc.tipo_doc);
                  return (
                    <div key={doc.tipo_doc}>
                      <Checkbox checked={isChecked} onChange={(e) => updateState((prev) => {
                            const updated = e.target.checked ? _.union(prev.selectTiposDocumentos, [doc.tipo_doc]) : _.without(prev.selectTiposDocumentos, doc.tipo_doc);
                            return { ...prev, selectTiposDocumentos: updated, filtersPend: true };
                          })
                        }
                      />
                      <span>{doc.tipo_doc}</span>
                    </div>
                  );
                })}
              </div>
            </Menu>

            {/* PALAVRAS DE ATENÇÃO */}
            <Tooltip title="Palavras de atenção" arrow>
              <IconButton onClick={(e) => updateState({ attWordMenu: e.currentTarget })} color={state.selectedAttWords.length > 0 ? "primary" : "default"}>
                <ErrorOutline />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={state.attWordMenu} open={!!state.attWordMenu} onClose={() => updateState({ attWordMenu: null })}>
              <D className="col e aF scD scB hv" style={{ maxHeight: "30vh", padding: "0.3rem 1rem 0.3rem 0.3rem" }}>
                {state.attWords.map((a) => {
                  const isChecked = state.selectedAttWords.includes(a);
                  return (
                    <D className="row aC" key={a}>
                      <Checkbox checked={isChecked} onChange={(e) => updateState((prev) => {
                            const updated = e.target.checked ? _.union(prev.selectedAttWords, [a]) : _.without(prev.selectedAttWords, a);
                            return { ...prev, selectedAttWords: updated, filtersPend: true };
                          })
                        }
                      />
                      <D e>{a}</D>
                    </D>
                  );
                })}
              </D>
            </Menu>

            {/* EMBARCAÇÕES */}
            <Tooltip title="Embarcações" arrow>
              <IconButton onClick={(e) => updateState({ embarcacoesMenu: e.currentTarget })} color={state.selectEmbarcacoes.length > 0 ? "primary" : "default"}>
                <DirectionsBoat />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={state.embarcacoesMenu} open={!!state.embarcacoesMenu} onClose={() => updateState({ embarcacoesMenu: null })}>
              <div style={{ maxHeight: "30vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
                {state.embarcacoes?.map((a) => {
                  const isChecked = state.selectEmbarcacoes.includes(a.nome);
                  return (
                    <div key={a.id}>
                      <Checkbox checked={isChecked} onChange={(e) => updateState((prev) => {
                            const updated = e.target.checked ? _.union(prev.selectEmbarcacoes, [a.nome]) : _.without(prev.selectEmbarcacoes, a.nome);
                            return { ...prev, selectEmbarcacoes: updated, filtersPend: true };
                          })
                        }
                      />
                      <span>{a.nome}</span>
                    </div>
                  );
                })}
              </div>
            </Menu>

            {/* GRUPOS */}
            <Tooltip title="Filial-grupo" arrow>
              <IconButton onClick={(e) => updateState({ groupsMenu: e.currentTarget })} color={state.selectGroups.length > 0 ? "primary" : "default"}>
                <SouthAmerica />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={state.groupsMenu} open={!!state.groupsMenu} onClose={() => updateState({ groupsMenu: null })}>
              <D className="col e aF scD scB hv" style={{ maxHeight: "30vh", padding: "0.2rem 1rem 0.2rem 0.2rem" }}>
                {state.groups.map((a) => {
                  const isChecked = state.selectGroups.includes(a);
                  return (
                    <D className="row aC" key={a}>
                      <Checkbox checked={isChecked} 
                        onChange={(e) => updateState((prev) => {
                            const updatedSelectGroups = e.target.checked ? _.union(prev.selectGroups, [a]) : _.without(prev.selectGroups, a);
                            return { ...prev, selectGroups: updatedSelectGroups, filtersPend: true };
                          })
                        }
                      />
                      <D e>{a}</D>
                    </D>
                  );
                })}
              </D>
            </Menu>

            {/* FORMATO DO DOCUMENTO */}
            <Tooltip title="Formato do documento" arrow>
              <IconButton onClick={(e) => updateState({ groupsTypeDocuments: e.currentTarget })} color={state.selectTypeDocuments.length > 0 ? "primary" : "default"}>
                <DescriptionOutlined />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={state.groupsTypeDocuments} open={!!state.groupsTypeDocuments} onClose={() => updateState({ groupsTypeDocuments: null })}>
              <D className="col e aF scD scB hv" style={{ maxHeight: "30vh", padding: "0.3rem 1rem 0.3rem 0.3rem" }}>
                {state.extensionsDocuments.map((a) => {
                  const isChecked = state.selectTypeDocuments.includes(a);
                  return (
                    <D className="row aC" key={a}>
                      <Checkbox checked={isChecked} onChange={(e) => updateState((prev) => {
                            const updated = e.target.checked ? _.union(prev.selectTypeDocuments, [a]) : _.without(prev.selectTypeDocuments, a);
                            // const next = { ...prev, selectTypeDocuments: updated };
                            return { ...prev, selectTypeDocuments: updated, filtersPend: true };
                          })
                        }
                      />
                      <D e>{a}</D>
                    </D>
                  );
                })}
              </D>
            </Menu>

            {/* =================================== 
              CONJUNTO DOS BOTOES DE AÇAO IMEDIATA 
              ===================================== */}
            <Divider sx={{ marginLeft: "0.3rem", marginRight: "0.3rem" }} orientation="vertical" flexItem />

            {/* LIMPAR FILTROS se tiver filtros ativos/selecionados */}
            {hasFilters && (
              <Tooltip title="Limpar filtros" arrow>
                <IconButton onClick={cleanFilter}>
                  <FilterAltOff color="action" />
                </IconButton>
              </Tooltip>
            )}

            {/* REFRESH  */}
            <Tooltip title={!state.filtersPend ? "Atualizar" : "Acionar busca"} arrow>
              <IconButton
                sx={{
                  backgroundColor: state.filtersPend ? "#d32f2f" : "transparent",
                  borderRadius: "4px", padding: "4px", transition: "all 0.2s ease-in-out",
                  "&:hover": { backgroundColor: state.filtersPend ? "#b71c1c" : "rgba(0, 0, 0, 0.04)" },
                }}
                onClick={executarBusca}
              >
                {!state.filtersPend ? <Refresh sx={{ color: "#6b6a6a", fontSize: "1.4rem" }} /> : <Search sx={{ color: "#fff", fontSize: "1.4rem" }} />}
              </IconButton>
            </Tooltip>

            {/* TODOS / NAO LIDOS */}
            <Tooltip title={state.unread ? "Todos" : "Documentos não lidos"} arrow>
              <IconButton 
                onClick={() => { 
                  updateState((p) => ({ unread: !p.unread, filtersPend: false })); 
                  getMediatecaPayload();
                }}
                color={state.unread ? "primary" : "default"}>
                <VisibilityOff />
              </IconButton>
            </Tooltip>

            {/* COM PENDÊNCIAS */}
            <Tooltip title={state.pendencias === 1 ? "Todos" : "Documentos c/ pendências"} arrow>
              <IconButton 
                onClick={() => { 
                  updateState((p) => ({ pendencias: p.pendencias === 1 ? 0 : 1, filtersPend: false, pr_om: 0 })); 
                  getMediatecaPayload(); 
                }} 
                color={state.pendencias === 1 ? "primary" : "default"}>
                <PendingActions />
              </IconButton>
            </Tooltip>

            {/* COM PR + OM */}
            <Tooltip title={state.pr_om === 1 ? "Todos" : "Documentos c/ PR ou OM"} arrow>
              <IconButton 
                onClick={() => { 
                  updateState((p) => ({ pr_om: Number(p.pr_om) === 1 ? 0 : 1, filtersPend: false, pendencias: 0 })); 
                  getMediatecaPayload(); 
                }} 
                color={state.pr_om === 1 ? "primary" : "default"}>
                <Build sx={{ transform: 'scaleX(-1)' }} />
              </IconButton>
            </Tooltip>

            {/* EDITADOS */}
            <Tooltip title={state.edt === 1 ? "Todos" : "Documentos editados"} arrow>
              <IconButton 
                onClick={() => { 
                  updateState((p) => ({ edt: Number(p.edt) === 1 ? 0 : 1, filtersPend: false,})); 
                  getMediatecaPayload(); 
                }} 
                color={state.edt === 1 ? "primary" : "default"}>
                <Edit  />
              </IconButton>
            </Tooltip>

            {/* SELECIONADOS */}
            {(state.selectedRows.length > 0 || state.selected === 1) && (
              <Tooltip title={state.selected === 1 ? "Todos" : "Documentos selecionados"} arrow>
                <IconButton 
                  onClick={() => { 
                    updateState((p) => ({ selected: p.selected === 1 ? 0 : 1, filtersPend: false, page: 1 })); 
                    getMediatecaPayload();
                  }} 
                  color={state.selected === 1 ? "primary" : "default"}>
                  <TaskAlt />
                </IconButton>
              </Tooltip>
            )}

          </div>

          {/* ============================================================== */}
          {/* 2º CONJUNTO: ALINHADO À DIREITA (Divider e Ações gerais)       */}
          {/* ============================================================== */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            
            {/* Divider marcando o início das ações à direita */}
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            {/* AÇÕES QDO TEM ITENS SELECIONADOS */}
            {state.selectedRows.length > 0 && (
              <>
                {props.permission === "w" && (
                  <>
                    <Tooltip title="Excluir Documentos" arrow>
                      <IconButton onClick={() =>
                          props.dialog(
                            <ConfirmDialog
                              title="Excluir Documentos"
                              msg={state.selectedRows.length > 1 ? `${state.selectedRows.length} documentos selecionados.` : "1 documento selecionado."}
                              onCancel={props.dialogClose}
                              onConfirm={() => {
                                const dcs = state.selectedRows.length === 0 ? state.fls.map((f) => f.doc_id) : state.selectedRows;
                                props.api.post("mediateca/delete", { docs: dcs }).then((r) => {
                                  updateState({ selectedRows: [] });
                                  props.dialogClose();
                                  props.alert(`Documentos excluídos com sucesso: ${r.success || 0}\nDocumentos com falha: ${r.failure || 0}`);
                                  getMediatecaPayload();
                                });
                              }}
                            />
                          )
                        }
                      >
                        <Delete />
                        {/* sx={{ color: '#d37e2f' }} */}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Reprocessar documentos" arrow>
                      <IconButton onClick={() =>
                          props.dialog(
                            <ConfirmDialog
                              title="Reprocessar Documentos"
                              msg="Ao confirmar, os metadados dos documentos selecionados serão recalculados."
                              reg={state.selectedRows.length > 1 ? `${state.selectedRows.length} documentos selecionados.` : "1 documento selecionado."}
                              onCancel={props.dialogClose}
                              onConfirm={() => {
                                const dcs = state.selectedRows.length === 0 ? state.fls.map((f) => f.doc_id) : state.selectedRows;
                                props.api.post("mediateca/reprocess", { docs: dcs }).then(() => {
                                  updateState({ selectedRows: [] });
                                  props.dialogClose();
                                  props.alert("Solicitação de reprocessamento enviada com sucesso.");
                                  getMediatecaPayload();
                                });
                              }}
                            />
                          )
                        }
                      >
                        <ModelTraining />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                <Tooltip title="Baixar documentos" arrow>
                  <IconButton onClick={() => {
                      const dcs = state.selectedRows.length === 0 ? state.fls.map((f) => f.doc_id) : state.selectedRows;
                      props.api.post("/mediateca/downloadzipfile", { docs: dcs }, { responseType: "arraybuffer" }).then((response) => {
                        updateState({ selectedRows: [] });
                        saveAs(new Blob([response], { type: "application/octet-stream" }), "selectedFiles.zip");
                      });
                    }}
                  >
                    <FileDownload />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* UPLOAD E EXPORT */}
            {props.permission === "w" && (
              <Tooltip title="Enviar documentos" arrow>
                <IconButton onClick={() => { updateState({ uploadExpanded: true }); inputFilesRef.current?.click(); }}>
                  <CloudUpload />
                </IconButton>
              </Tooltip>
            )}

            {state.count > 0 && (
              <>
                <Tooltip title="Exportar para Excel" arrow>
                  <IconButton size="large" sx={{ color: "#106436ff" }} onClick={() => {
                      if (state.count > LARGE_MEDIATECA_THRESHOLD) updateState({ showLargeMediatecaWarning: true });
                      else exportToExcel({ title: `Mediateca: ${state.count === 1 ? "1 Documento" : `${new Intl.NumberFormat("pt-BR").format(state.count)} Documentos`}` });
                    }}
                  >
                    <TableChartOutlined />
                  </IconButton>
                </Tooltip>

                <Tooltip title={state.viewType === "icons" ? "Modo lista" : "Modo Cards"} arrow>
                  <IconButton onClick={() => updateState((p) => ({ viewType: p.viewType === "icons" ? "list" : "icons", selectedRows: [] }))}>
                    {state.viewType === "list" ? <GridView /> : <ViewList />}
                  </IconButton>
                </Tooltip>
              </>
            )}

          </div>
        </D>

        {/* ÁREA DE CONTEUDO DO RESULTADO FORMATO LISTA OU GRID */}
        <D className="row e aF scD">
          {state.viewType === "list" ? (
            <MediatecaList
              fls={state.fls}
              api={props.api}
              getMediatecaPayload={getMediatecaPayload}
              selectedRows={state.selectedRows}
              searchField={state.searchField.toLowerCase()}
              getInfo={getInfo}
              onRowClick={(i) =>
                updateState((p) => ({
                  selectedRows: p.selectedRows.includes(i)
                  ? p.selectedRows.filter((id) => id !== i)
                  : [...p.selectedRows, i],
                }))
              }
              onUnselectAll={() => updateState({ selectedRows: [] })}
              permission={props.permission}
              order={state.order}
              orderBy={state.orderBy}
              updateOrder_OrderBy={(o, b) => updateState({ order: o, orderBy: b, page: 1 })}
              isLoading={state.isLoading}
            />
          ) : (
            <MediatecaGrid
              fls={state.fls}
              getInfo={getInfo}
              selectedRows={state.selectedRows}
              onClick={(i) => updateState({ selectedRows: [i] })}
            />
          )}

          {/* JANELA LATERAL MAIS INFO */}
          {state.isInfoOpen && state.selectedRows.length > 0 && (
            <MediatecaInfo
              key={state.selectedRows[0]}
              file={_.find(state.fls, { doc_id: state.selectedRows[0] })}
              facts={state.facts}
              embarcacoes={state.embarcacoes?.map((e) => e.nome) || []} // passa somente os nomes 
              grupos={state.groups}
              closeInfo={closeInfo}
              dialog={props.dialog}
              dialogClose={props.dialogClose}
              alert={props.alert}
              api={props.api}
              permission={props.permission}
              role={props.user.role}
              updateMediateca={getMediatecaPayload}
            />
          )}
        </D>

        {/* RODAPÉ E PAGINAÇÃO */}
        <Box sx={{ display: "flex", justifyContent: "space-between", marginRight: "20px", marginTop: "5px" }}>
          <Box
            sx={{ fontWeight: "normal", marginLeft: "25px", display: "flex", alignItems: "center", minWidth: "220px" }}
          >
            {state.selectedRows.length > 0
              ? state.selectedRows.length === 1
                ? "1 Documento selecionado"
                : `${new Intl.NumberFormat("pt-BR").format(state.selectedRows.length)} Documentos selecionados`
              : null}
          </Box>
          <Box sx={{ fontWeight: "normal", marginLeft: "25px", display: "flex", alignItems: "center" }}>
            {state.count > 0
              ? state.count === 1
                ? "1 Documento encontrado"
                : `${new Intl.NumberFormat("pt-BR").format(state.count)} Documentos encontrados`
              : null}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <InputLabel id="page-size-select-label" htmlFor="page-size-select-input">
                Documentos
              </InputLabel>
              <Select
                labelId="page-size-select-label" // <-- 2. Avisamos ao Select quem é o label dele
                id="page-size-select"
                name="pageSize"
                label="Documentos"
                value={state.pageSize}
                onChange={(e) => updateState({ pageSize: e.target.value, page: 1 })}
                inputProps={{
                  id: 'page-size-select-input',  // Injeta o ID diretamente no <input> escondido
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Pagination
              count={limit}
              page={state.page}
              size="medium"
              variant="outlined"
              shape="rounded"
              onChange={(_, p) => updateState({ page: p })}
            />
          </Box>
        </Box>

        {/* MODAIS AVISO */}
        {state.showLargeMediatecaWarning && (
          <Dialog
            open={state.showLargeMediatecaWarning}
            onClose={() => updateState({ showLargeMediatecaWarning: false })}
          >
            <DialogTitle>Atenção</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Esta seleção contém <b>{new Intl.NumberFormat("pt-BR").format(state.count)}</b> documentos.<br />
                A operação pode demorar para carregar.<br /><br />
                Deseja continuar mesmo assim?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => updateState({ showLargeMediatecaWarning: false })}>Cancelar</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  updateState({ showLargeMediatecaWarning: false });
                  exportToExcel({
                    title: `Mediateca: ${state.count === 1 ? "1 Documento" : `${new Intl.NumberFormat("pt-BR").format(state.count)} Documentos`}`,
                  });
                }}
              >
                Continuar
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* MODAL DE UPLOAD */}
        {state.visibleUploads && state.visibleUploads.length > 0 && (
          <D
            col
            style={{
              zIndex: "1051",
              position: "fixed",
              bottom: "1rem",
              marginLeft: "1rem",
              minWidth: "200px",
              width: "100%",
              maxWidth: "350px",
            }}
          >
            <Paper className="col e aF" elevation={2}>
              <Accordion
                expanded={state.uploadExpanded}
                onChange={() => updateState((p) => ({ uploadExpanded: !p.uploadExpanded }))}
                sx={{ boxShadow: "none", border: "1px solid #3C4252", borderRadius: "4px" }}
              >
                <AccordionSummary
                  sx={{
                    backgroundColor: "#3C4252",
                    borderRadius: "4px 4px 0px 0px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: "8px",
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    },
                  }}
                >
                  <Typography sx={{ color: "#fff", fontWeight: 500 }}>Upload de documentos</Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <DarkTooltip title={state.uploadExpanded ? "Minimizar" : "Expandir"}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateState((p) => ({ uploadExpanded: !p.uploadExpanded }));
                        }}
                        sx={{ color: "#ccc", "&:hover": { color: "#fff" }, ml: 1 }}
                      >
                        {!state.uploadExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </DarkTooltip>
                    <DarkTooltip title="Fechar">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateState({ visibleUploads: [] });
                        }}
                        sx={{ color: "#ccc", "&:hover": { color: "#fff" }, ml: 1 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </DarkTooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails 
                  sx={{ padding: "16px" }}>
                  {state.visibleUploads.map((item) => (
                    <UploadListCard 
                      key={item.file.name + item.createdAt?.getTime()} 
                      title={item.file.name} 
                      status={item.status} 
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            </Paper>
          </D>
        )}

        <input
          className="hide"
          type="file"
          name="file"
          ref={inputFilesRef}
          multiple="multiple"
          onChange={uploadFiles}
        />
        <canvas className="hide" />
      </D>
    </>
  );
};
